export const PDF_DOWNLOAD_LINK_EXPIRED_TITLE = "Download link expired";

export const PDF_DOWNLOAD_LINK_EXPIRED_MESSAGE =
  "This download link has expired. Please generate the report again to get a new file.";

export class PdfDownloadLinkExpiredError extends Error {
  readonly title = PDF_DOWNLOAD_LINK_EXPIRED_TITLE;

  constructor(message: string = PDF_DOWNLOAD_LINK_EXPIRED_MESSAGE) {
    super(message);
    this.name = "PdfDownloadLinkExpiredError";
  }
}

/** Read the SAS expiry (`se`) query param from an Azure blob download URL. */
export function getPdfDownloadUrlExpiry(url: string): Date | null {
  try {
    const expiryParam = new URL(url).searchParams.get("se");
    if (!expiryParam) return null;

    const expiry = new Date(expiryParam);
    return Number.isFinite(expiry.getTime()) ? expiry : null;
  } catch {
    return null;
  }
}

export function isPdfDownloadUrlExpired(url: string, now = Date.now()): boolean {
  const expiry = getPdfDownloadUrlExpiry(url);
  return expiry !== null && expiry.getTime() <= now;
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(anchor);
}

function triggerDirectNavigation(url: string): void {
  window.location.assign(url);
}

function isBlobAccessDeniedStatus(status: number): boolean {
  // 403/404: expired or invalid SAS; 409: account blocks anonymous access (no SAS)
  return status === 403 || status === 404 || status === 409;
}

/**
 * Download a generated PDF or ZIP from an Azure blob SAS URL.
 * Checks SAS expiry client-side, then fetches via CORS when allowed.
 * Falls back to direct navigation when fetch is blocked (e.g. tenant origins
 * not on the storage account CORS list).
 */
export async function downloadFromBlobUrl(
  url: string,
  filename: string,
): Promise<void> {
  if (isPdfDownloadUrlExpired(url)) {
    throw new PdfDownloadLinkExpiredError();
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      if (isBlobAccessDeniedStatus(response.status)) {
        throw new PdfDownloadLinkExpiredError();
      }
      throw new Error(`Download failed (${response.status})`);
    }

    const blob = await response.blob();
    triggerBrowserDownload(blob, filename);
  } catch (error) {
    if (error instanceof PdfDownloadLinkExpiredError) {
      throw error;
    }

    // CORS/network — preserve legacy window.location behaviour for batch ZIPs.
    if (error instanceof TypeError) {
      triggerDirectNavigation(url);
      return;
    }

    throw error;
  }
}

/**
 * Filename from Content-Disposition. Prefer RFC 5987 filename* so characters
 * outside Latin-1 (an en dash in a factsheet name) are kept. filename= is the
 * ASCII fallback the API sends so the header itself can be transmitted.
 */
export function filenameFromContentDisposition(
  contentDisposition: string | undefined,
  fallback: string,
): string {
  if (!contentDisposition) return fallback;

  const encoded = /filename\*=(?:UTF-8|utf-8)''([^;]+)/i.exec(contentDisposition);
  if (encoded?.[1]) {
    try {
      const decoded = decodeURIComponent(encoded[1].trim().replace(/"/g, ""));
      if (decoded) return decoded;
    } catch {
      // Malformed percent-encoding; use the ASCII filename or fallback.
    }
  }

  const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quoted?.[1]) return quoted[1];

  const plain = /(?:^|;)\s*filename=([^;]+)/i.exec(contentDisposition);
  if (plain?.[1]) {
    const name = plain[1].trim().replace(/^"|"$/g, "");
    if (name) return name;
  }

  return fallback;
}

export function getPdfDownloadErrorMessage(error: unknown): string {
  if (error instanceof PdfDownloadLinkExpiredError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Download failed. Please try again.";
}
